import { motion } from 'framer-motion';
import { ChefHat, Heart, Leaf, Sparkles, Users } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_chaat-fresh/artifacts/22020fsm_2%20bowl%20final.png';

export default function About() {
  return (
    <div className="min-h-screen py-24 bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-bold text-brown-dark mb-4">
            About Us
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <img
              src={LOGO_URL}
              alt="Chaat-De-licious Logo"
              className="w-full max-w-md mx-auto"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h2 className="text-3xl sm:text-4xl font-playfair font-bold text-spice">
              Gourmet Chaat - Reinvented
            </h2>
            <p className="text-lg text-brown-medium leading-relaxed">
              At Chaat-De-licious, we bring a premium twist to India's most loved fun food. Our cloud kitchen creates gourmet-style chaat that is healthy, hygienic, and irresistibly delicious using fresh fruits, high-fiber ingredients, and protein-rich elements.
            </p>
            <p className="text-lg text-brown-medium leading-relaxed">
              We carefully craft every dish using fresh fruits, high-fiber ingredients, and protein-rich elements, transforming traditional chaat into a wholesome and satisfying meal.
            </p>
            <p className="text-lg text-brown-medium leading-relaxed">
              To complement our vibrant dishes, we serve unique summer coolers, crafted with refreshing fruits and natural ingredients—perfectly paired to elevate your chaat experience.
            </p>
            <p className="text-xl font-playfair font-bold text-leaf">
              At Chaat-De-licious, we believe that indulgence and health can go hand in hand.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30"
          >
            <div className="w-16 h-16 bg-turmeric rounded-full flex items-center justify-center mx-auto mb-4">
              <ChefHat size={32} className="text-brown-dark" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
              Gourmet Quality
            </h3>
            <p className="text-brown-medium text-sm">
              Premium ingredients crafted with love and expertise
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30"
          >
            <div className="w-16 h-16 bg-leaf rounded-full flex items-center justify-center mx-auto mb-4">
              <Leaf size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
              Fresh & Healthy
            </h3>
            <p className="text-brown-medium text-sm">
              High-fiber, protein-rich ingredients for guilt-free indulgence
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30"
          >
            <div className="w-16 h-16 bg-spice rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={32} className="text-white" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
              Made with Love
            </h3>
            <p className="text-brown-medium text-sm">
              Every dish prepared with passion and care
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30"
          >
            <div className="w-16 h-16 bg-turmeric rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles size={32} className="text-brown-dark" />
            </div>
            <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
              Hygienic Kitchen
            </h3>
            <p className="text-brown-medium text-sm">
              Cloud kitchen ensuring the highest standards of cleanliness
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-spice/10 to-leaf/10 rounded-2xl p-12 text-center"
        >
          <Users size={48} className="text-spice mx-auto mb-6" />
          <h2 className="text-3xl font-playfair font-bold text-brown-dark mb-4">
            Our Mission
          </h2>
          <p className="text-xl text-brown-medium max-w-3xl mx-auto leading-relaxed">
            Chaat-De-licious — where fun food meets gourmet innovation — fresh, flavourful with Love!
          </p>
        </motion.div>
      </div>
    </div>
  );
}